import { useFetch } from "./use-fetch";
import {
  AccountDetailInfo,
  Bot,
  BotCreate,
  BotResponse,
  MarketConnect,
  MarketEdit,
  NotificationResponse,
  StrategiesResponse,
  StrategyCreate,
  StrategyTypeResponse,
  TickerResponse,
  UserResponse,
  UserSettings,
} from "../_app/types";
import { Strategy } from "../store/slices/strategySlice";
import { encryptWithAES } from "../_utils/encryptWithAES";
import { encryptWithRSA } from "../_utils/encryptWithRSA";
import { useSubscriptionApi } from "../_app/api/subscription/subscription-api";
import { useAccountApi } from "../_app/api/account/account-api";

export const useApi = () => {
  const fetch = useFetch();

  return {
    subscription: useSubscriptionApi(),

    account: useAccountApi(),

    getPublicKey: async (): Promise<any> => {
      return await fetch(`${process.env.REACT_APP_API_ACCOUNT_MANAGER}/keys/public`, "GET");
    },

    connectNewAccount: async (profileId: string, data: MarketConnect): Promise<any> => {
      const publicKey: any = await fetch(
        `${process.env.REACT_APP_API_ACCOUNT_MANAGER}/keys/public`,
        "GET"
      );

      const encryptedMarketSecurityProps = await encryptWithAES(
        JSON.stringify(data.marketSecurityProps)
      );

      const encryptedData = await encryptWithRSA(
        encryptedMarketSecurityProps.key,
        encryptedMarketSecurityProps.iv,
        publicKey.public
      );

      return await fetch(
        `${process.env.REACT_APP_API_ACCOUNT_MANAGER}/profile/${profileId}/link?isEncrypted=true&rsaAlgorithm=OAEP`,
        "POST",
        {
          name: data.name,
          market: data.market,
          leverage: data.leverage,
          encryptedAccountData: {
            a: encryptedData.encryptedAesKey,
            b: encryptedMarketSecurityProps.encryptedStr,
            c: encryptedData.encryptedIv,
          },
        },
        { platform: true, xPublicKey: publicKey.public }
      );
    },

    getAccountInformation: async (accountId: string): Promise<AccountDetailInfo> => {
      return await fetch(
        `${process.env.REACT_APP_API_ACCOUNT_MANAGER}/profile/account/${accountId}/information`,
        "GET",
        undefined,
        { platform: true }
      );
    },

    changeAccountSettings: async (accountId: string, marketEdit: MarketEdit): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_ACCOUNT_MANAGER}/profile/account/${accountId}`,
        "PATCH",
        marketEdit,
        { platform: true }
      );
    },

    connectAccount: async (profileId: string, market: MarketConnect): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_ACCOUNT_MANAGER}/profile/${profileId}/link`,
        "POST",
        market,
        { platform: true }
      );
    },

    getOrders: async (botId: string, market: string): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_BOT_MANAGER}/orders?botId=${botId}&market=${market}&page=0&size=10000`,
        "GET"
      );
    },

    botTypes: async (profileId: string, accountId?: string): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_BOT_MANAGER}/bots/types?profileId=${profileId}${
          accountId !== undefined ? `&accountId=${accountId}` : ""
        }`,
        "GET"
      );
    },

    strategyTypes: async (profileId: string, accountId?: string): Promise<StrategyTypeResponse> => {
      return await fetch(
        `${process.env.REACT_APP_API_STRATEGY}/strategy/types?profileId=${profileId}${
          accountId !== undefined ? `&accountId=${accountId}` : ""
        }`,
        "GET"
      );
    },

    getBotsStrategy: async (
      id: string,
      sort = ["status:asc", "closeTime:desc"],
      page = 0,
      size = 35
    ): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_BOT_MANAGER}/bots/strategy/${id}?sort=${sort}&page=${page}&size=${size}`,
        "GET"
      );
    },

    getBots: async (
      accountId: string,
      status: string,
      profileId: string,
      page = 0,
      sort = "createdAt:desc",
      type = "FUTURES_GRID",
      size = 10
    ): Promise<BotResponse> => {
      return await fetch(
        `${
          process.env.REACT_APP_API_BOT_MANAGER
        }/bots?profileId=${profileId}&accountId=${accountId}&page=${page}&size=${size}&sort=${encodeURIComponent(
          sort
        )}&status=${status}&type=${type}`,
        "GET"
      );
    },

    getStrategyLogs: async (
      strategyId: string,
      start = 0,
      limit = 50,
      sort = "createdAt:desc"
    ): Promise<NotificationResponse> => {
      return await fetch(
        `${process.env.REACT_APP_API_NOTIFICATION}/api/v1/notification/strategy?strategyId=${strategyId}&start=${start}&limit=${limit}&sort=${sort}`,
        "GET"
      );
    },

    getStrategies: async (
      accountId: string,
      profileId: string,
      status: string,
      page = 0,
      sort = "startTime:asc",
      size = 20
    ): Promise<StrategiesResponse> => {
      return await fetch(
        `${
          process.env.REACT_APP_API_STRATEGY
        }/strategy?profileId=${profileId}&accountId=${accountId}&status=${status}&page=${page}&size=${size}&sort=${encodeURIComponent(
          sort
        )}`,
        "GET"
      );
    },

    getStrategy: async (id: string): Promise<Strategy> => {
      return await fetch(`${process.env.REACT_APP_API_STRATEGY}/strategy/${id}`, "GET");
    },

    getBotById: async (id: string): Promise<Bot> => {
      return await fetch(`${process.env.REACT_APP_API_BOT_MANAGER}/bots/${id}`, "GET");
    },

    launchCreateBot: async (bot: BotCreate): Promise<any> => {
      return await fetch(`${process.env.REACT_APP_API_BOT_MANAGER}/bots`, "POST", bot);
    },

    closeBot: async (
      botId: string,
      profileId: string,
      accountId: string,
      closeOption: string
    ): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_BOT_MANAGER}/bots/${botId}/close?profileId=${profileId}&accountId=${accountId}&closeOption=${closeOption}`,
        "POST"
      );
    },

    getBotLogs: async (
      botId: string,
      start = 0,
      limit = 50,
      sort = "createdAt:desc"
    ): Promise<NotificationResponse> => {
      return await fetch(
        `${process.env.REACT_APP_API_NOTIFICATION}/api/v1/notification/bot?botId=${botId}&start=${start}&limit=${limit}&sort=${sort}`,
        "GET"
      );
    },

    closeStrategy: async (strategyId: string, closeOption: string): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_STRATEGY}/strategy/${strategyId}/close?closeOption=${closeOption}`,
        "POST"
      );
    },

    launchStrategy: async (strategy: StrategyCreate) => {
      return await fetch(`${process.env.REACT_APP_API_STRATEGY}/strategy`, "POST", strategy);
    },

    updateUserSettings: async (profileId: string, settings: UserSettings[]): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_ACCOUNT_MANAGER}/profile/${profileId}/props`,
        "PATCH",
        settings,
        { platform: true }
      );
    },

    getTickers: async (
      exchange = "BYBIT",
      type = "FUTURES",
      page = 0,
      size = 20
    ): Promise<TickerResponse> => {
      return await fetch(
        `${process.env.REACT_APP_API_DATA_PROVIDER}/tickers?exchange=${exchange}&type=${type}&page=${page}&size=${size}`,
        "GET"
      );
    },

    accountNotifications: async (
      accountId: string,
      start = 0,
      limit = 50,
      sort = "createdAt:desc"
    ): Promise<NotificationResponse> => {
      return await fetch(
        `${process.env.REACT_APP_API_NOTIFICATION}/api/v1/notification/account?accountId=${accountId}&start=${start}&limit=${limit}&sort=${sort}`,
        "GET"
      );
    },

    createUserProfile: async (): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_ACCOUNT_MANAGER}/profile`,
        "POST",
        undefined,
        { tg: true }
      );
    },

    getUserProfile: async (id?: string): Promise<UserResponse> => {
      return await fetch(
        `${process.env.REACT_APP_API_ACCOUNT_MANAGER}/profile/${id}`,
        "GET",
        undefined,
        { platform: true }
      );
    },

    deleteAccount: async (accountId: string): Promise<any> => {
      return await fetch(
        `${process.env.REACT_APP_API_ACCOUNT_MANAGER}/profile/account/${accountId}`,
        "DELETE",
        undefined,
        { platform: true }
      );
    },
  };
};
